<template>
    <Head :title="isEditing ? 'Edit Incident' : 'Report Incident'" />

    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <!-- Header -->
        <div class="flex items-center justify-between mb-6">
            <div class="flex items-center space-x-4">
                <Link
                    :href="route('incidents.index')"
                    class="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                >
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                    </svg>
                </Link>
                <div class="flex items-center gap-3">
                    <div>
                        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white">
                            {{ isEditing ? 'Edit Incident' : 'Report Incident or Near Miss' }}
                        </h2>
                        <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                            Help us improve overall operations by reporting incidents and near misses
                        </p>
                    </div>
                    <InfoButton title="Reporting Incidents & Near Misses" variant="default">
                        <p>
                            <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                            The Incident Reporting page allows you to report safety, security, operational, and
                            environmental incidents, as well as near misses. Incident reporting is critical for
                            identifying hazards, preventing future occurrences, and maintaining regulatory compliance.
                            This form captures comprehensive details for proper investigation and corrective action.
                            All reports are automatically assigned unique tracking numbers and flow into the incident
                            management workflow for triage, investigation, and resolution.
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                            • <strong>Anonymous Reporting:</strong> Check this option if you wish to report without
                              revealing your identity. Your name will be hidden from all users and communications<br>
                            • <strong>Report Type:</strong> Choose between Incident (actual harm/loss occurred) or
                              Near Miss (potential harm prevented). Near misses are valuable for hazard identification<br>
                            • <strong>Title:</strong> Provide a clear, concise summary (e.g., "Slip hazard in
                              warehouse aisle 3")<br>
                            • <strong>Incident Type:</strong> Select the appropriate category from Safety, Security,
                              Service & Operations, Environmental, HR & Compliance, or Other. Type description provides
                              additional context<br>
                            • <strong>Location:</strong> Specify where the incident occurred. This helps with
                              location-based analysis<br>
                            • <strong>Incident Date/Time:</strong> When did the incident actually occur? Be as accurate
                              as possible<br>
                            • <strong>Discovered Date/Time:</strong> Optional - if the incident was found later, specify
                              when it was first noticed<br>
                            • <strong>Description:</strong> Provide detailed information including what happened, the
                              circumstances, immediate response taken, and any relevant context (minimum 20 characters)<br>
                            • <strong>People Involved:</strong> Optional - add names, roles, and contact information for
                              anyone directly involved or who witnessed the incident<br>
                            • <strong>Attachments:</strong> Upload photos, documents, or diagrams to support your report
                              (if available in your subscription plan)
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                            • <strong>Report promptly</strong> - Fresh details lead to better investigations<br>
                            • <strong>Be specific in descriptions</strong> - Include the 5 W's: Who, What, When, Where, Why<br>
                            • <strong>Anonymous reporting encourages honesty</strong> - Use this feature when you're
                              concerned about potential consequences<br>
                            • <strong>Near miss reporting prevents incidents</strong> - Don't hesitate to report
                              close calls; they're learning opportunities<br>
                            • <strong>Include environmental factors</strong> - Weather, lighting, noise levels, time of
                              day can all be relevant<br>
                            • <strong>Upload photos if possible</strong> - Visual documentation is invaluable for
                              investigations<br>
                            • <strong>List witnesses</strong> - This helps investigators gather complete information<br>
                            • <strong>Describe immediate actions</strong> - Document what was done right after the
                              incident (first aid, securing the area, etc.)<br>
                            • Reports are submitted with "Open" status and will be triaged by managers to determine
                              appropriate response (no action, quick fix, or full investigation)
                        </p>
                    </InfoButton>
                </div>
            </div>
        </div>

        <form @submit.prevent="submitForm" class="space-y-6">
            <!-- Incident Classification -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Incident Classification</h3>

                <!-- Anonymous Reporting Toggle -->
                <div class="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <div class="flex items-start">
                        <div class="flex items-center h-5">
                            <input
                                v-model="form.is_anonymous"
                                type="checkbox"
                                id="is_anonymous"
                                class="h-4 w-4 text-yellow-600 dark:text-yellow-500 focus:ring-yellow-500 border-gray-300 dark:border-gray-600 rounded"
                            />
                        </div>
                        <div class="ml-3">
                            <label for="is_anonymous" class="font-medium text-gray-900 dark:text-yellow-300">
                                Report Anonymously
                            </label>
                            <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                Your identity will be kept confidential. The report will show as "Anonymous" and will not include your name in any communications.
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Report Type Selection -->
                <div class="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <label class="block text-sm font-medium text-gray-900 dark:text-white mb-3">
                        Report Type <span class="text-red-500">*</span>
                    </label>

                    <div class="space-y-4">
                        <!-- Report Incident Option -->
                        <div class="flex items-start">
                            <div class="flex items-center h-5 mt-1">
                                <input
                                    v-model="form.is_near_miss"
                                    :value="false"
                                    type="radio"
                                    id="report_incident"
                                    name="report_type"
                                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600"
                                />
                            </div>
                            <div class="ml-3">
                                <label for="report_incident" class="font-medium text-gray-900 dark:text-white cursor-pointer">
                                    Report Incident
                                </label>
                                <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                    An incident is an unplanned event that resulted in injury, illness, damage to property, or environmental harm. This includes any event where actual harm or loss occurred.
                                </p>
                            </div>
                        </div>

                        <!-- Report Near Miss Option -->
                        <div class="flex items-start">
                            <div class="flex items-center h-5 mt-1">
                                <input
                                    v-model="form.is_near_miss"
                                    :value="true"
                                    type="radio"
                                    id="report_near_miss"
                                    name="report_type"
                                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600"
                                />
                            </div>
                            <div class="ml-3">
                                <label for="report_near_miss" class="font-medium text-gray-900 dark:text-white cursor-pointer">
                                    Report Near Miss
                                </label>
                                <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                    A near miss is an unplanned event that <strong>did not</strong> result in injury, illness, or damage, but had the potential to do so. Reporting near misses helps identify hazards and prevent future incidents before harm occurs.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Basic Information -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Incident Details</h3>
                <div class="space-y-6">
                    <!-- Title -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Title <span class="text-red-500">*</span>
                        </label>
                        <input
                            v-model="form.title"
                            type="text"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            :class="{ 'border-red-500': form.errors.title }"
                            placeholder="Brief description of the incident"
                            required
                        />
                        <p v-if="form.errors.title" class="mt-1 text-sm text-red-600">{{ form.errors.title }}</p>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <!-- Incident Type -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Incident Type <span class="text-red-500">*</span>
                            </label>
                            <select
                                v-model="form.type_id"
                                class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                :class="{ 'border-red-500': form.errors.type_id }"
                                required
                            >
                                <option value="">Select Type</option>
                                <optgroup v-for="(types, category) in groupedIncidentTypes" :key="category" :label="category">
                                    <option v-for="type in types" :key="type.id" :value="type.id">
                                        {{ type.name }}
                                    </option>
                                </optgroup>
                            </select>
                            <p v-if="form.errors.type_id" class="mt-1 text-sm text-red-600">{{ form.errors.type_id }}</p>
                            <p v-if="selectedType?.description" class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                {{ selectedType.description }}
                            </p>
                        </div>

                        <!-- Location -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Location <span class="text-red-500">*</span>
                            </label>
                            <select
                                v-model="form.location_id"
                                class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                :class="{ 'border-red-500': form.errors.location_id }"
                                required
                            >
                                <option value="">Select Location</option>
                                <option v-for="location in locations" :key="location.id" :value="location.id">
                                    {{ location.name }}
                                </option>
                            </select>
                            <p v-if="form.errors.location_id" class="mt-1 text-sm text-red-600">{{ form.errors.location_id }}</p>
                        </div>

                        <!-- Reported At -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Incident Date/Time <span class="text-red-500">*</span>
                            </label>
                            <input
                                v-model="form.reported_at"
                                type="datetime-local"
                                class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                :class="{ 'border-red-500': form.errors.reported_at }"
                                required
                            />
                            <p v-if="form.errors.reported_at" class="mt-1 text-sm text-red-600">{{ form.errors.reported_at }}</p>
                            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">When did the incident occur?</p>
                        </div>

                        <!-- Discovered At -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Discovered Date/Time
                            </label>
                            <input
                                v-model="form.discovered_at"
                                type="datetime-local"
                                class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                :class="{ 'border-red-500': form.errors.discovered_at }"
                            />
                            <p v-if="form.errors.discovered_at" class="mt-1 text-sm text-red-600">{{ form.errors.discovered_at }}</p>
                            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">When was it first noticed? (if different)</p>
                        </div>
                    </div>

                    <!-- Description -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Description <span class="text-red-500">*</span>
                        </label>
                        <textarea
                            v-model="form.description"
                            rows="6"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            :class="{ 'border-red-500': form.errors.description }"
                            placeholder="Provide a detailed description including: What happened? When did it occur? Where did it happen? What were the circumstances? What immediate actions were taken?"
                            required
                        ></textarea>
                        <p v-if="form.errors.description" class="mt-1 text-sm text-red-600">{{ form.errors.description }}</p>
                        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Minimum 20 characters. Be as detailed as possible.</p>
                    </div>
                </div>
            </div>

            <!-- People Involved -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">People Involved (Optional)</h3>
                <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">Add anyone who was directly involved or witnessed the incident</p>

                <div class="space-y-4">
                    <div
                        v-for="(person, index) in form.people_involved"
                        :key="index"
                        class="border border-gray-200 rounded-lg p-4"
                    >
                        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Name *</label>
                                <input
                                    v-model="person.name"
                                    type="text"
                                    class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                    placeholder="Person's name"
                                />
                            </div>
                            <div>
                                <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Role</label>
                                <input
                                    v-model="person.role"
                                    type="text"
                                    class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                    placeholder="e.g., Employee, Contractor, Witness"
                                />
                            </div>
                            <div>
                                <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Contact</label>
                                <div class="flex space-x-2">
                                    <input
                                        v-model="person.contact"
                                        type="text"
                                        class="flex-1 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                        placeholder="Phone or email"
                                    />
                                    <button
                                        type="button"
                                        @click="removePerson(index)"
                                        class="text-red-600 hover:text-red-800"
                                    >
                                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <button
                        type="button"
                        @click="addPerson"
                        class="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-blue-500 hover:text-blue-600 transition"
                    >
                        + Add Person
                    </button>
                </div>
            </div>

            <!-- File Attachments -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Attachments (Optional)</h3>

                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Upload Documents (Images/PDFs)
                    </label>

                    <!-- Document Upload Enabled -->
                    <div v-if="canUploadDocuments">
                        <div class="flex items-center justify-center w-full">
                            <label
                                for="attachments"
                                class="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600"
                            >
                                <div class="flex flex-col items-center justify-center pt-5 pb-6">
                                    <svg class="w-8 h-8 mb-3 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                    </svg>
                                    <p class="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                        <span class="font-semibold">Click to upload</span> or drag and drop
                                    </p>
                                    <p class="text-xs text-gray-500 dark:text-gray-400">Images (PNG, JPG) or PDF files (Max 10MB each)</p>
                                </div>
                                <input
                                    id="attachments"
                                    type="file"
                                    multiple
                                    accept="image/jpeg,image/jpg,image/png,application/pdf"
                                    @change="onFileChange"
                                    class="hidden"
                                />
                            </label>
                        </div>

                        <!-- File Preview -->
                        <div v-if="selectedFiles.length > 0" class="mt-4 space-y-2">
                            <div
                                v-for="(file, index) in selectedFiles"
                                :key="index"
                                class="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                            >
                                <div class="flex items-center space-x-3">
                                    <svg v-if="file.type.startsWith('image/')" class="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd" />
                                    </svg>
                                    <svg v-else class="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd" />
                                    </svg>
                                    <div>
                                        <p class="text-sm font-medium text-gray-900 dark:text-white">{{ file.name }}</p>
                                        <p class="text-xs text-gray-500 dark:text-gray-400">{{ formatFileSize(file.size) }}</p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    @click="removeFile(index)"
                                    class="text-red-600 hover:text-red-800"
                                >
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Document Upload Disabled - Show Upgrade Message -->
                    <div v-else class="flex items-center justify-center w-full">
                        <div class="w-full p-6 border-2 border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                            <div class="flex items-start space-x-3">
                                <svg class="w-6 h-6 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                                </svg>
                                <div class="flex-1">
                                    <h3 class="text-sm font-semibold text-amber-900 dark:text-amber-300 mb-1">Document Upload Not Available</h3>
                                    <p class="text-sm text-amber-800 dark:text-amber-400">
                                        Document upload is not included in your company's subscription plan.
                                        Contact your company administrator for more information about upgrading your plan.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <p v-if="form.errors.attachments" class="mt-1 text-sm text-red-600">{{ form.errors.attachments }}</p>
                </div>
            </div>

            <!-- Form Actions -->
            <div class="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                <Link
                    :href="route('incidents.index')"
                    class="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                    Cancel
                </Link>
                <button
                    type="submit"
                    :disabled="form.processing"
                    class="px-6 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                    <svg v-if="form.processing" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {{ form.processing ? 'Submitting...' : (isEditing ? 'Update Incident' : 'Submit Report') }}
                </button>
            </div>
        </form>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { Head, Link, useForm } from '@inertiajs/vue3';
import route from '@/Utils/route';
import { useSecureForm } from '@/Composables/useSecureForm';
import { useToast } from '@/Composables/useToast';
import InfoButton from '@/Components/Common/InfoButton.vue';

const toast = useToast();

const props = defineProps({
    incident: {
        type: Object,
        default: null,
    },
    incidentTypes: {
        type: Array,
        required: true,
    },
    locations: {
        type: Array,
        required: true,
    },
    canUploadDocuments: {
        type: Boolean,
        default: false,
    },
    companyOwner: {
        type: Object,
        default: null,
    },
});

const isEditing = computed(() => !!props.incident);

// Initialize form with useSecureForm for company context protection
const form = useSecureForm({
    title: props.incident?.title || '',
    description: props.incident?.description || '',
    type_id: props.incident?.type_id || '',
    location_id: props.incident?.location_id || '',
    is_near_miss: props.incident?.is_near_miss || false,
    is_anonymous: props.incident?.is_anonymous || false,
    reported_at: props.incident?.reported_at || '',
    discovered_at: props.incident?.discovered_at || '',
    people_involved: props.incident?.people_involved || [],
    attachments: [],
});

// File management
const selectedFiles = ref([]);

// Group incident types by category
const groupedIncidentTypes = computed(() => {
    const groups = {};
    const categoryLabels = {
        'safety': 'Safety',
        'security': 'Security',
        'service': 'Service & Operations',
        'environmental': 'Environmental',
        'hr': 'HR & Compliance',
        'other': 'Other'
    };

    props.incidentTypes.forEach(type => {
        const category = type.category || 'other';
        const label = categoryLabels[category.toLowerCase()] || category;
        if (!groups[label]) {
            groups[label] = [];
        }
        groups[label].push(type);
    });
    return groups;
});

// Selected type
const selectedType = computed(() => {
    return props.incidentTypes.find(t => t.id === form.type_id);
});

// People management
const addPerson = () => {
    form.people_involved.push({
        name: '',
        role: '',
        contact: '',
    });
};

const removePerson = (index) => {
    form.people_involved.splice(index, 1);
};

// File handling
const onFileChange = (event) => {
    const files = Array.from(event.target.files);

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    const validFiles = files.filter(file => {
        if (file.size > maxSize) {
            toast.error(`File ${file.name} is too large. Maximum size is 10MB.`);
            return false;
        }
        return true;
    });

    selectedFiles.value = [...selectedFiles.value, ...validFiles];
    form.attachments = selectedFiles.value;

    // Reset input
    event.target.value = '';
};

const removeFile = (index) => {
    selectedFiles.value.splice(index, 1);
    form.attachments = selectedFiles.value;
};

const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

// Submit form
const submitForm = () => {
    const url = isEditing.value
        ? route('incidents.update', props.incident.id)
        : route('incidents.store');

    // DEBUG: Log form data being submitted
    console.log('=== INCIDENT FORM SUBMISSION DEBUG ===');
    console.log('URL:', url);
    console.log('Form Data:', {
        title: form.title,
        description: form.description,
        type_id: form.type_id,
        location_id: form.location_id,
        is_near_miss: form.is_near_miss,
        is_anonymous: form.is_anonymous,
        reported_at: form.reported_at,
        discovered_at: form.discovered_at,
        people_involved: form.people_involved,
        attachments: form.attachments,
    });

    if (isEditing.value) {
        form.put(url, {
            preserveScroll: true,
            onSuccess: (response) => {
                console.log('✓ Form submitted successfully:', response);
            },
            onError: (errors) => {
                console.error('✗ VALIDATION ERRORS:', errors);
                console.error('Form errors object:', form.errors);
            },
            onFinish: () => {
                console.log('Form submission finished (processing complete)');
            },
        });
    } else {
        form.post(url, {
            preserveScroll: true,
            onSuccess: (response) => {
                console.log('✓ Form submitted successfully:', response);
            },
            onError: (errors) => {
                console.error('✗ VALIDATION ERRORS:', errors);
                console.error('Form errors object:', form.errors);
                console.error('Full form state:', {
                    data: form.data(),
                    processing: form.processing,
                    wasSuccessful: form.wasSuccessful,
                    recentlySuccessful: form.recentlySuccessful,
                });
            },
            onFinish: () => {
                console.log('Form submission finished (processing complete)');
            },
        });
    }
};
</script>
